/**
 * Canvas text only picks up a webfont once that exact font/weight combination has
 * finished loading, so every draw path waits on the CSS Font Loading API first.
 * Results are cached because `document.fonts.load` is called on every re-render.
 */
const inFlight = new Map<string, Promise<void>>();

export function fontSpec(family: string, weight: number, sizePx = 100): string {
  return `${weight} ${sizePx}px "${family}"`;
}

export function ensureFont(family: string, weight: number): Promise<void> {
  const key = `${family}|${weight}`;
  const cached = inFlight.get(key);
  if (cached) return cached;

  const load = (async () => {
    if (typeof document === 'undefined' || !document.fonts) return;
    try {
      await document.fonts.load(fontSpec(family, weight));
    } catch {
      // A missing or blocked webfont is not fatal — canvas falls back to sans-serif.
    }
  })();

  inFlight.set(key, load);
  return load;
}

export function ensureFonts(specs: { family: string; weight: number }[]): Promise<void[]> {
  return Promise.all(specs.map((s) => ensureFont(s.family, s.weight)));
}

export function isFontAvailable(family: string, weight: number): boolean {
  if (typeof document === 'undefined' || !document.fonts) return true;
  try {
    return document.fonts.check(fontSpec(family, weight));
  } catch {
    return true;
  }
}
